import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Plus, Clock, ArrowLeft, Palmtree, Trash2, Pencil, ChevronDown, ChevronUp, Calendar, Info, CalendarOff, CalendarDays } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { TimeSlotDialog } from "@/components/TimeSlotDialog";
import { TimeSlotImpactDialog } from "@/components/TimeSlotImpactDialog";
import { TimeSlotClosureDialog } from "@/components/TimeSlotClosureDialog";
import { OnlineClosuresCard } from "@/components/OnlineClosuresCard";
import { DateOverrideSlotDialog } from "@/components/DateOverrideSlotDialog";
import { ClosurePeriodDialog } from "@/components/ClosurePeriodDialog";
import { ClosureImpactDialog } from "@/components/ClosureImpactDialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Database } from "@/integrations/supabase/types";
import { 
  checkTimeSlotModificationImpact, 
  checkTimeSlotDeletionImpact, 
  checkTimeSlotDeactivationImpact,
  ImpactedReservationByTimeSlot 
} from "@/lib/timeSlotImpactUtils";

import { checkClosureImpact, ClosureImpact } from "@/lib/closureImpactUtils";

import { useEstablishmentClosures, EstablishmentClosure } from "@/hooks/useEstablishmentClosures";
import { useTimeSlotClosures, TimeSlotClosure } from "@/hooks/useTimeSlotClosures";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { parseDateLocal, formatDateLocal } from "@/lib/timeFormatUtils";
import { filterSlotsWithoutClosure } from "@/lib/timeSlotClosureUtils";

type TimeSlot = Database['public']['Tables']['time_slots']['Row'];

const DAYS_OF_WEEK = [
  "Dimanche",
  "Lundi", 
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi"
];

export default function TimeSlots() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { establishmentId } = useAuth();
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<TimeSlot | null>(null);
  
  // Reservation settings for booking mode indicator
  const { settings: reservationSettings } = useReservationSettings({ 
    establishmentId: establishmentId || undefined 
  });
  
  // Closures
  const { 
    closures, 
    loading: closuresLoading, 
    addClosure, 
    updateClosure, 
    deleteClosure,
    getActiveClosures,
    getPastClosures 
  } = useEstablishmentClosures({ establishmentId: establishmentId || undefined });
  
  // Time Slot Closures (fermetures de service)
  const {
    closures: slotClosures,
    loading: slotClosuresLoading,
    addClosure: addSlotClosure,
    addClosures: addSlotClosures,
    updateClosure: updateSlotClosure,
    deleteClosure: deleteSlotClosure,
    getActiveClosures: getActiveSlotClosures,
    getPastClosures: getPastSlotClosures,
  } = useTimeSlotClosures({ establishmentId: establishmentId || undefined });
  
  const [isSlotClosureDialogOpen, setIsSlotClosureDialogOpen] = useState(false);
  const [editingSlotClosure, setEditingSlotClosure] = useState<TimeSlotClosure | null>(null);
  const [preselectedSlotId, setPreselectedSlotId] = useState<string | undefined>();
  const [showPastSlotClosures, setShowPastSlotClosures] = useState(false);
  
  const [isClosureDialogOpen, setIsClosureDialogOpen] = useState(false);
  const [editingClosure, setEditingClosure] = useState<EstablishmentClosure | null>(null);
  const [showPastClosures, setShowPastClosures] = useState(false);

  // Date overrides state
  const [dateOverrides, setDateOverrides] = useState<any[]>([]);
  const [dateOverridesLoading, setDateOverridesLoading] = useState(true);
  const [isDateOverrideDialogOpen, setIsDateOverrideDialogOpen] = useState(false);
  const [editingDateOverride, setEditingDateOverride] = useState<any | null>(null);
  
  // Impact dialog state
  const [showImpactDialog, setShowImpactDialog] = useState(false);
  const [impactData, setImpactData] = useState<{
    timeSlotLabel: string;
    impactedReservations: ImpactedReservationByTimeSlot[];
    actionType: 'modify' | 'delete' | 'deactivate';
  } | null>(null);
  
  // Pending action state for confirmation
  const [pendingSlotAction, setPendingSlotAction] = useState<{
    type: 'modify' | 'delete' | 'deactivate';
    data?: { 
      id?: string;
      startTime: string; 
      endTime: string; 
      availableDays: number[];
      isActive: boolean;
    };
    slotId?: string;
    slot?: TimeSlot;
  } | null>(null);

  // Closure impact dialog state
  const [showClosureImpactDialog, setShowClosureImpactDialog] = useState(false);
  const [closureImpactData, setClosureImpactData] = useState<ClosureImpact | null>(null);
  const [pendingClosure, setPendingClosure] = useState<{
    startDate: string;
    endDate: string;
    reason?: string;
    customerMessage?: string;
    isEditing: boolean;
    closureId?: string;
  } | null>(null);
  const [closureImpactLoading, setClosureImpactLoading] = useState(false);


  const fetchTimeSlots = async () => {
    try {
      let query = supabase
        .from('time_slots')
        .select('*')
        .order('start_time');

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTimeSlots(data || []);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les créneaux.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDateOverrides = async () => {
    if (!establishmentId) return;
    try {
      const { data, error } = await supabase
        .from('time_slot_date_overrides')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      if (error) throw error;
      setDateOverrides(data || []);
    } catch (error) {
      console.error('Error fetching date overrides:', error);
    } finally {
      setDateOverridesLoading(false);
    }
  };

  useEffect(() => {
    if (establishmentId) {
      fetchTimeSlots();
      fetchDateOverrides();
    }
  }, [establishmentId]);

  const executeModification = async (data: { 
    id?: string;
    startTime: string; 
    endTime: string; 
    availableDays: number[];
    isActive: boolean;
    durationOverrideMinutes?: number | null;
  }) => {
    if (!data.id) return;
    
    const { error } = await supabase
      .from('time_slots')
      .update({
        start_time: data.startTime,
        end_time: data.endTime,
        available_days: data.availableDays,
        is_active: data.isActive,
        duration_override_minutes: data.durationOverrideMinutes ?? null
      } as any)
      .eq('id', data.id);

    if (error) throw error;

    toast({
      title: "Créneau modifié",
      description: "Le créneau a été modifié avec succès.",
    });
    
    await fetchTimeSlots();
    setIsDialogOpen(false);
    setEditingSlot(null);
  };

  const executeDelete = async (slotId: string) => {
    const { error } = await supabase
      .from('time_slots')
      .delete()
      .eq('id', slotId);

    if (error) throw error;

    await fetchTimeSlots();
    toast({
      title: "Créneau supprimé",
      description: "Le créneau a été supprimé avec succès.",
    });
  };

  const executeDeactivation = async (slot: TimeSlot) => {
    const { error } = await supabase
      .from('time_slots')
      .update({ is_active: !slot.is_active })
      .eq('id', slot.id);

    if (error) throw error;

    await fetchTimeSlots();
    toast({
      title: slot.is_active ? "Créneau désactivé" : "Créneau activé",
      description: `${slot.start_time} - ${slot.end_time}`,
    });
  };

  const handleConfirmPendingAction = async () => {
    if (!pendingSlotAction) return;
    
    try {
      if (pendingSlotAction.type === 'modify' && pendingSlotAction.data) {
        await executeModification(pendingSlotAction.data);
      } else if (pendingSlotAction.type === 'delete' && pendingSlotAction.slotId) {
        await executeDelete(pendingSlotAction.slotId);
      } else if (pendingSlotAction.type === 'deactivate' && pendingSlotAction.slot) {
        await executeDeactivation(pendingSlotAction.slot);
      }
    } catch (error) {
      console.error('Error executing pending action:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'effectuer l'action.",
        variant: "destructive",
      });
    } finally {
      setPendingSlotAction(null);
      setImpactData(null);
    }
  };

  const handleSubmitSlot = async (data: { 
    id?: string;
    startTime: string; 
    endTime: string; 
    availableDays: number[];
    isActive: boolean;
    durationOverrideMinutes?: number | null;
  }) => {
    try {
      if (data.id) {
        // MODIFICATION - Vérifier l'impact
        const existingSlot = timeSlots.find(s => s.id === data.id);
        if (existingSlot) {
          const impact = await checkTimeSlotModificationImpact(
            data.id,
            existingSlot.start_time,
            data.startTime,
            data.availableDays,
            establishmentId || undefined,
            existingSlot.end_time,
            data.endTime,
            existingSlot.available_days
          );
          
          if (impact.hasImpactedReservations) {
            // Stocker l'action en attente et afficher l'avertissement
            setPendingSlotAction({ type: 'modify', data });
            setImpactData({
              timeSlotLabel: `${existingSlot.start_time.substring(0, 5)} - ${existingSlot.end_time.substring(0, 5)}`,
              impactedReservations: impact.impactedReservations,
              actionType: 'modify',
            });
            setShowImpactDialog(true);
            return;
          }
        }

        // Pas d'impact, exécuter directement
        await executeModification(data);
      } else {
        // Création - pas de vérification d'impact
        const { error } = await supabase
          .from('time_slots')
          .insert({
            start_time: data.startTime,
            end_time: data.endTime,
            available_days: data.availableDays,
            is_active: data.isActive,
            establishment_id: establishmentId || null,
            duration_override_minutes: data.durationOverrideMinutes ?? null
          } as any);

        if (error) throw error;

        toast({
          title: "Créneau ajouté",
          description: "Le créneau a été créé avec succès.",
        });
        
        await fetchTimeSlots();
        setIsDialogOpen(false);
        setEditingSlot(null);
      }
    } catch (error) {
      console.error('Error saving time slot:', error);
      toast({
        title: "Erreur",
        description: data.id ? "Impossible de modifier le créneau." : "Impossible de créer le créneau.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    const slot = timeSlots.find(s => s.id === slotId);
    if (!slot) return;

    const impact = await checkTimeSlotDeletionImpact(slotId, slot.start_time, establishmentId || undefined, slot.end_time);
    
    if (impact.hasImpactedReservations) {
      // Stocker l'action en attente et afficher l'avertissement
      setPendingSlotAction({ type: 'delete', slotId });
      setImpactData({
        timeSlotLabel: `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}`,
        impactedReservations: impact.impactedReservations,
        actionType: 'delete',
      });
      setShowImpactDialog(true);
      return;
    }

    // Pas d'impact, demander confirmation simple
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce créneau ?")) return;

    try {
      await executeDelete(slotId);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le créneau.",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (slot: TimeSlot) => {
    // Si on désactive, vérifier l'impact
    if (slot.is_active) {
      const impact = await checkTimeSlotDeactivationImpact(slot.id, slot.start_time, establishmentId || undefined, slot.end_time);
      
      if (impact.hasImpactedReservations) {
        // Stocker l'action en attente et afficher l'avertissement
        setPendingSlotAction({ type: 'deactivate', slot });
        setImpactData({
          timeSlotLabel: `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}`,
          impactedReservations: impact.impactedReservations,
          actionType: 'deactivate',
        });
        setShowImpactDialog(true);
        return;
      }
    }

    // Si on réactive ou si pas d'impact, continuer
    try {
      await executeDeactivation(slot);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de modifier le créneau.",
        variant: "destructive",
      });
    }
  };

  const handleEditReservation = (reservationId: string) => {
    // Naviguer vers le dashboard avec l'ID de réservation pour l'éditer
    navigate(`/dashboard?editReservation=${reservationId}`);
  };

  // --- Date Override CRUD ---
  const handleAddDateOverride = async (data: {
    date: string;
    startTime: string;
    endTime: string;
    durationOverrideMinutes?: number | null;
    label?: string;
    isActive: boolean;
  }) => {
    if (!establishmentId) return;
    try {
      const { error } = await supabase
        .from('time_slot_date_overrides')
        .insert({
          establishment_id: establishmentId,
          date: data.date,
          start_time: data.startTime,
          end_time: data.endTime,
          duration_override_minutes: data.durationOverrideMinutes ?? null,
          label: data.label || null,
          is_active: data.isActive,
        } as any);
      if (error) throw error;
      toast({ title: "Créneau spécifique ajouté", description: `${data.startTime} - ${data.endTime} le ${data.date}` });
      await fetchDateOverrides();
    } catch (error) {
      console.error('Error adding date override:', error);
      toast({ title: "Erreur", description: "Impossible d'ajouter le créneau spécifique.", variant: "destructive" });
    }
  };

  const handleEditDateOverride = async (data: {
    date: string;
    startTime: string;
    endTime: string;
    durationOverrideMinutes?: number | null;
    label?: string;
    isActive: boolean;
  }) => {
    if (!editingDateOverride?.id) return;
    try {
      const { error } = await supabase
        .from('time_slot_date_overrides')
        .update({
          date: data.date,
          start_time: data.startTime,
          end_time: data.endTime,
          duration_override_minutes: data.durationOverrideMinutes ?? null,
          label: data.label || null,
          is_active: data.isActive,
        } as any)
        .eq('id', editingDateOverride.id);
      if (error) throw error;
      toast({ title: "Créneau spécifique modifié" });
      setEditingDateOverride(null);
      await fetchDateOverrides();
    } catch (error) {
      console.error('Error updating date override:', error);
      toast({ title: "Erreur", description: "Impossible de modifier le créneau spécifique.", variant: "destructive" });
    }
  };

  const handleDeleteDateOverride = async (id: string) => {
    if (!confirm("Supprimer ce créneau spécifique ?")) return;
    try {
      const { error } = await supabase
        .from('time_slot_date_overrides')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast({ title: "Créneau spécifique supprimé" });
      await fetchDateOverrides();
    } catch (error) {
      console.error('Error deleting date override:', error);
      toast({ title: "Erreur", description: "Impossible de supprimer le créneau spécifique.", variant: "destructive" });
    }
  };

  // Group date overrides by date
  const dateOverridesByDate = useMemo(() => {
    const grouped: Record<string, typeof dateOverrides> = {};
    for (const o of dateOverrides) {
      const d = o.date;
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(o);
    }
    return grouped;
  }, [dateOverrides]);

  const activeSlots = timeSlots.filter(s => s.is_active);
  const inactiveSlots = timeSlots.filter(s => !s.is_active);

  // Grouper par type : généraux (tous les jours) vs spécifiques
  const generalSlots = timeSlots.filter(s => s.available_days.length === 0);
  const specificSlots = timeSlots.filter(s => s.available_days.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold">Gestion des créneaux</h1>
              <p className="text-muted-foreground text-sm sm:text-base hidden sm:block">
                Configurez les plages horaires de réservation
              </p>
            </div>
          </div>
          <Button 
            onClick={() => {
              setEditingSlot(null);
              setIsDialogOpen(true);
            }}
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="sm:inline">Nouveau créneau</span>
          </Button>
        </div>

        {/* Booking Mode Indicator */}
        {reservationSettings && (
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-800 dark:text-blue-300">
              Mode de réservation actif
            </AlertTitle>
            <AlertDescription className="text-blue-700 dark:text-blue-400">
              {reservationSettings.bookingMode === 'time' ? (
                <>
                  <strong>Mode horaires</strong> : Les clients peuvent réserver toutes les{' '}
                  <strong>{reservationSettings.bookingIntervalMinutes} minutes</strong> à l'intérieur de vos créneaux. 
                  Durée des réservations : <strong>{reservationSettings.reservationDurationMinutes} min</strong>.
                </>
              ) : (
                <>
                  <strong>Mode créneaux</strong> : Les clients réservent au début de chaque créneau défini ci-dessous.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Closure Periods Section */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Palmtree className="w-5 h-5" />
              Périodes de fermeture
            </CardTitle>
            <Button onClick={() => {
              setEditingClosure(null);
              setIsClosureDialogOpen(true);
            }} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Bloquez les réservations pendant vos vacances ou fermetures exceptionnelles
            </p>

            {/* Active/Upcoming Closures */}
            {getActiveClosures().length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Palmtree className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune période de fermeture programmée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getActiveClosures().map((closure) => {
                  const startDate = parseDateLocal(closure.start_date);
                  const endDate = parseDateLocal(closure.end_date);
                  const todayStr = formatDateLocal(new Date());
                  const isActive = todayStr >= closure.start_date && todayStr <= closure.end_date;
                  
                  return (
                    <div
                      key={closure.id}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        isActive 
                          ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700' 
                          : 'bg-white/50 dark:bg-black/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-muted-foreground'}`} />
                        <div>
                          <p className="font-medium">
                            {closure.reason || 'Fermeture'}
                            {isActive && (
                              <Badge variant="secondary" className="ml-2 bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                En cours
                              </Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {format(startDate, "d MMMM yyyy", { locale: fr })} → {format(endDate, "d MMMM yyyy", { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingClosure(closure);
                            setIsClosureDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteClosure(closure.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Past Closures */}
            {getPastClosures().length > 0 && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between text-muted-foreground"
                  onClick={() => setShowPastClosures(!showPastClosures)}
                >
                  <span>Périodes passées ({getPastClosures().length})</span>
                  {showPastClosures ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                
                {showPastClosures && (
                  <div className="space-y-2 mt-2">
                    {getPastClosures().map((closure) => (
                      <div
                        key={closure.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{closure.reason || 'Fermeture'}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(closure.start_date), "d MMM yyyy", { locale: fr })} → {format(new Date(closure.end_date), "d MMM yyyy", { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteClosure(closure.id)}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Closures Section */}
        <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarOff className="w-5 h-5" />
                Fermetures de service
              </CardTitle>
              <CardDescription className="mt-1">
                Bloquez un créneau spécifique pour une période (ex: service du soir le 14 février)
              </CardDescription>
            </div>
            <Button onClick={() => {
              setEditingSlotClosure(null);
              setPreselectedSlotId(undefined);
              setIsSlotClosureDialogOpen(true);
            }} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Bloquer
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Active/Upcoming Slot Closures */}
            {getActiveSlotClosures().length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune fermeture de service programmée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getActiveSlotClosures().map((closure) => {
                  const startDate = parseDateLocal(closure.start_date);
                  const endDate = parseDateLocal(closure.end_date);
                  const todayStr = formatDateLocal(new Date());
                  const isActive = todayStr >= closure.start_date && todayStr <= closure.end_date;
                  
                  // Find the time slot label
                  const slot = timeSlots.find(s => s.id === closure.time_slot_id);
                  const slotLabel = slot 
                    ? `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}`
                    : 'Créneau supprimé';
                  
                  return (
                    <div
                      key={closure.id}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        isActive 
                          ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700' 
                          : 'bg-white/50 dark:bg-black/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Clock className={`w-5 h-5 ${isActive ? 'text-red-600' : 'text-muted-foreground'}`} />
                        <div>
                          <p className="font-medium">
                            {slotLabel}
                            {isActive && (
                              <Badge variant="secondary" className="ml-2 bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200">
                                En cours
                              </Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {format(startDate, "d MMMM yyyy", { locale: fr })} → {format(endDate, "d MMMM yyyy", { locale: fr })}
                          </p>
                          {closure.reason && (
                            <p className="text-xs text-muted-foreground italic mt-0.5">{closure.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingSlotClosure(closure);
                            setIsSlotClosureDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteSlotClosure(closure.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Past Slot Closures */}
            {getPastSlotClosures().length > 0 && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between text-muted-foreground"
                  onClick={() => setShowPastSlotClosures(!showPastSlotClosures)}
                >
                  <span>Blocages passés ({getPastSlotClosures().length})</span>
                  {showPastSlotClosures ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                
                {showPastSlotClosures && (
                  <div className="space-y-2 mt-2">
                    {getPastSlotClosures().map((closure) => {
                      const slot = timeSlots.find(s => s.id === closure.time_slot_id);
                      const slotLabel = slot 
                        ? `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}`
                        : 'Créneau supprimé';
                      
                      return (
                        <div
                          key={closure.id}
                          className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
                        >
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">
                                {slotLabel} {closure.reason && `— ${closure.reason}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(closure.start_date), "d MMM yyyy", { locale: fr })} → {format(new Date(closure.end_date), "d MMM yyyy", { locale: fr })}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteSlotClosure(closure.id)}
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fermetures à la réservation en ligne (time_slot_blocks) */}
        <OnlineClosuresCard establishmentId={establishmentId} />

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium">Total</CardTitle>
              <Clock className="w-4 h-4 text-muted-foreground hidden sm:block" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold">{timeSlots.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium">Actifs</CardTitle>
              <Clock className="w-4 h-4 text-green-600 hidden sm:block" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold">{activeSlots.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium">Inactifs</CardTitle>
              <Clock className="w-4 h-4 text-muted-foreground hidden sm:block" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold">{inactiveSlots.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Time Slots List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Créneaux horaires
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timeSlots.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Aucun créneau configuré
              </p>
            ) : (
              <div className="grid gap-3">
                {timeSlots.map((slot) => {
                  const dayLabels = slot.available_days.length === 0
                    ? "Tous les jours"
                    : slot.available_days.map(d => DAYS_OF_WEEK[d]).join(", ");
                  
                  return (
                    <div
                      key={slot.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-3"
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <Clock className="w-5 h-5 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold">
                            {slot.start_time.substring(0, 5)} - {slot.end_time.substring(0, 5)}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {dayLabels}
                            {(slot as any).duration_override_minutes && (
                              <span className="ml-2 text-xs font-medium text-primary">
                                ⏱ {(slot as any).duration_override_minutes} min
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-between sm:justify-end">
                        <div className="flex items-center gap-2">
                          <Badge variant={slot.is_active ? "default" : "secondary"} className="text-xs">
                            {slot.is_active ? "Actif" : "Inactif"}
                          </Badge>
                          <Switch
                            checked={slot.is_active}
                            onCheckedChange={() => handleToggleActive(slot)}
                          />
                        </div>
                        {/* Desktop buttons */}
                        <div className="hidden sm:flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPreselectedSlotId(slot.id);
                              setEditingSlotClosure(null);
                              setIsSlotClosureDialogOpen(true);
                            }}
                          >
                            <CalendarOff className="w-4 h-4 mr-1" />
                            Bloquer
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingSlot(slot);
                              setIsDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSlot(slot.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                        {/* Mobile buttons */}
                        <div className="flex sm:hidden items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setPreselectedSlotId(slot.id);
                              setEditingSlotClosure(null);
                              setIsSlotClosureDialogOpen(true);
                            }}
                          >
                            <CalendarOff className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingSlot(slot);
                              setIsDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSlot(slot.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Date-specific overrides section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Créneaux spécifiques par date
                </CardTitle>
                <CardDescription className="mt-1">
                  Remplacent les créneaux habituels pour une date précise (ex : brunch spécial, événement).
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => { setEditingDateOverride(null); setIsDateOverrideDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {dateOverridesLoading ? (
              <p className="text-muted-foreground text-center py-4">Chargement...</p>
            ) : Object.keys(dateOverridesByDate).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Aucun créneau spécifique configuré
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(dateOverridesByDate)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([dateStr, overrides]) => {
                    const dateObj = new Date(dateStr + 'T00:00:00');
                    const isPast = dateObj < new Date(new Date().setHours(0, 0, 0, 0));
                    return (
                      <div key={dateStr} className={`border rounded-lg p-4 ${isPast ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            {format(dateObj, "EEEE d MMMM yyyy", { locale: fr })}
                            {isPast && <Badge variant="secondary" className="text-[10px]">Passé</Badge>}
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {overrides.map((override: any) => (
                            <div key={override.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                              <div className="flex items-center gap-3">
                                <Clock className="w-4 h-4 text-primary" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {override.start_time?.substring(0, 5)} - {override.end_time?.substring(0, 5)}
                                    {override.duration_override_minutes && (
                                      <span className="ml-2 text-xs text-primary">⏱ {override.duration_override_minutes} min</span>
                                    )}
                                  </p>
                                  {override.label && (
                                    <p className="text-xs text-muted-foreground italic">{override.label}</p>
                                  )}
                                </div>
                                <Badge variant={override.is_active ? "default" : "secondary"} className="text-[10px]">
                                  {override.is_active ? "Actif" : "Inactif"}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="icon" onClick={() => {
                                  setEditingDateOverride(override);
                                  setIsDateOverrideDialogOpen(true);
                                }}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteDateOverride(override.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TimeSlotDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setEditingSlot(null);
        }}
        onSubmit={handleSubmitSlot}
        editingSlot={editingSlot ? {
          id: editingSlot.id,
          startTime: editingSlot.start_time,
          endTime: editingSlot.end_time,
          availableDays: editingSlot.available_days,
          isActive: editingSlot.is_active ?? true,
          durationOverrideMinutes: (editingSlot as any).duration_override_minutes ?? null
        } : null}
        existingSlots={timeSlots.map(s => ({
          id: s.id,
          start_time: s.start_time,
          end_time: s.end_time,
          available_days: s.available_days
        }))}
        bookingMode={reservationSettings?.bookingMode}
        defaultDurationMinutes={reservationSettings?.reservationDurationMinutes}
      />

      {impactData && (
        <TimeSlotImpactDialog
          open={showImpactDialog}
          onOpenChange={(open) => {
            setShowImpactDialog(open);
            if (!open) {
              setPendingSlotAction(null);
              setImpactData(null);
            }
          }}
          timeSlotLabel={impactData.timeSlotLabel}
          impactedReservations={impactData.impactedReservations}
          actionType={impactData.actionType}
          onEditReservation={handleEditReservation}
          onConfirm={handleConfirmPendingAction}
        />
      )}

      <ClosurePeriodDialog
        open={isClosureDialogOpen}
        onOpenChange={(open) => {
          setIsClosureDialogOpen(open);
          if (!open) setEditingClosure(null);
        }}
        onSubmit={async (startDate, endDate, reason, customerMessage) => {
          if (!establishmentId) return;
          
          // Vérifier l'impact sur les réservations existantes
          try {
            const impact = await checkClosureImpact(
              startDate, 
              endDate, 
              establishmentId,
              editingClosure?.id
            );
            
            if (impact.hasImpact) {
              // Il y a des réservations impactées, afficher le dialog d'avertissement
              setClosureImpactData(impact);
              setPendingClosure({
                startDate,
                endDate,
                reason,
                customerMessage,
                isEditing: !!editingClosure,
                closureId: editingClosure?.id,
              });
              setIsClosureDialogOpen(false);
              setShowClosureImpactDialog(true);
              return;
            }
            
            // Pas d'impact, procéder directement
            if (editingClosure) {
              await updateClosure(editingClosure.id, startDate, endDate, reason, customerMessage);
            } else {
              await addClosure(startDate, endDate, reason, customerMessage);
            }
          } catch (error) {
            console.error('Erreur vérification impact fermeture:', error);
            toast({
              title: "Erreur",
              description: "Impossible de vérifier l'impact de la fermeture.",
              variant: "destructive",
            });
          }
        }}
        initialData={editingClosure ? {
          startDate: editingClosure.start_date,
          endDate: editingClosure.end_date,
          reason: editingClosure.reason || undefined,
          customerMessage: editingClosure.customer_message || undefined,
        } : undefined}
        mode={editingClosure ? 'edit' : 'create'}
      />

      {closureImpactData && pendingClosure && (
        <ClosureImpactDialog
          open={showClosureImpactDialog}
          onOpenChange={(open) => {
            setShowClosureImpactDialog(open);
            if (!open) {
              setClosureImpactData(null);
              setPendingClosure(null);
            }
          }}
          onConfirm={async () => {
            if (!pendingClosure) return;
            
            setClosureImpactLoading(true);
            try {
              if (pendingClosure.isEditing && pendingClosure.closureId) {
                await updateClosure(
                  pendingClosure.closureId,
                  pendingClosure.startDate,
                  pendingClosure.endDate,
                  pendingClosure.reason,
                  pendingClosure.customerMessage
                );
              } else {
                await addClosure(
                  pendingClosure.startDate,
                  pendingClosure.endDate,
                  pendingClosure.reason,
                  pendingClosure.customerMessage
                );
              }
              setShowClosureImpactDialog(false);
              setClosureImpactData(null);
              setPendingClosure(null);
              setEditingClosure(null);
            } catch (error) {
              console.error('Erreur création/modification fermeture:', error);
            } finally {
              setClosureImpactLoading(false);
            }
          }}
          impactedReservations={closureImpactData.impactedReservations}
          closurePeriod={{
            startDate: pendingClosure.startDate,
            endDate: pendingClosure.endDate,
            reason: pendingClosure.reason,
          }}
          loading={closureImpactLoading}
          isEditing={pendingClosure.isEditing}
        />
      )}

      <TimeSlotClosureDialog
        open={isSlotClosureDialogOpen}
        onOpenChange={(open) => {
          setIsSlotClosureDialogOpen(open);
          if (!open) {
            setEditingSlotClosure(null);
            setPreselectedSlotId(undefined);
          }
        }}
        onSubmit={async (timeSlotIds, startDate, endDate, reason, customerMessage) => {
          if (editingSlotClosure) {
            const timeSlotId = timeSlotIds[0];
            if (!timeSlotId) return;
            await updateSlotClosure(editingSlotClosure.id, timeSlotId, startDate, endDate, reason, customerMessage);
          } else {
            const idsToCreate = filterSlotsWithoutClosure(timeSlotIds, slotClosures, startDate, endDate);
            const skippedCount = timeSlotIds.length - idsToCreate.length;
            if (timeSlotIds.length === 1 && idsToCreate.length === 1) {
              await addSlotClosure(idsToCreate[0], startDate, endDate, reason, customerMessage);
            } else if (idsToCreate.length > 0) {
              await addSlotClosures(idsToCreate, startDate, endDate, reason, customerMessage, skippedCount);
            } else {
              toast({ title: "Créneaux déjà bloqués", description: "Tous les créneaux sélectionnés sont déjà fermés pour cette période." });
            }
          }
        }}
        initialData={editingSlotClosure ? {
          timeSlotId: editingSlotClosure.time_slot_id,
          startDate: editingSlotClosure.start_date,
          endDate: editingSlotClosure.end_date,
          reason: editingSlotClosure.reason || undefined,
          customerMessage: editingSlotClosure.customer_message || undefined,
        } : undefined}
        mode={editingSlotClosure ? 'edit' : 'create'}
        timeSlots={timeSlots.map(s => ({
          id: s.id,
          label: `${s.start_time.substring(0, 5)} - ${s.end_time.substring(0, 5)}`,
          startTime: s.start_time.substring(0, 5),
          endTime: s.end_time.substring(0, 5),
        }))}
        existingClosures={slotClosures}
        preselectedSlotId={preselectedSlotId}
      />

      <DateOverrideSlotDialog
        open={isDateOverrideDialogOpen}
        onOpenChange={(open) => {
          setIsDateOverrideDialogOpen(open);
          if (!open) setEditingDateOverride(null);
        }}
        onSubmit={editingDateOverride ? handleEditDateOverride : handleAddDateOverride}
        initialData={editingDateOverride ? {
          id: editingDateOverride.id,
          date: editingDateOverride.date,
          startTime: editingDateOverride.start_time?.substring(0, 5),
          endTime: editingDateOverride.end_time?.substring(0, 5),
          durationOverrideMinutes: editingDateOverride.duration_override_minutes,
          label: editingDateOverride.label,
          isActive: editingDateOverride.is_active,
        } : undefined}
        defaultDuration={reservationSettings?.reservationDurationMinutes}
        closedDates={Array.from(new Set(slotClosures.flatMap(closure => {
          const dates: string[] = [];
          const current = parseDateLocal(closure.start_date);
          const end = parseDateLocal(closure.end_date);
          while (current <= end) {
            dates.push(formatDateLocal(current));
            current.setDate(current.getDate() + 1);
          }
          return dates;
        })))}
      />
    </div>
  );
}
